import { slicedToArray as _slicedToArray, extends as _extends, objectWithoutProperties as _objectWithoutProperties } from '../../_virtual/_rollupPluginBabelHelpers.js';
import _debounce from 'lodash/debounce';
import { Carousel, StyledCarouselScrollContainer, token, StyledCarouselSlide, Box, IconButton, IconChevronLeft, IconChevronRight } from '@zillow/constellation';
import PropTypes from 'prop-types';
import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import PostCard from '../PostCard/PostCard.js';

var _excluded = ["children"];
var StyledCarousel = /*#__PURE__*/styled(Carousel).withConfig({
  componentId: "sc-1k647xh-0"
})(["", "{margin:0 -", "px;}", "{flex-basis:", ";}"], StyledCarouselScrollContainer, token('spacing.sm'), StyledCarouselSlide, function (props) {
  return Number.isInteger(props.slideWidth) ? props.slideWidth + "px" : props.slideWidth;
});
var CustomCarousel = function CustomCarousel(_ref) {
  var children = _ref.children,
    props = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      margin: '-18px 0 $spacing.lg 0'
    }
  }, /*#__PURE__*/React.createElement(StyledCarousel, props, children));
};
CustomCarousel.propTypes = process.env.NODE_ENV !== "production" ? {
  children: PropTypes.node.isRequired
} : {};
function getViewPort(view) {
  // eslint-disable-next-line no-undef
  if (view.innerWidth < 768) {
    return 'mobile';
  }
  // eslint-disable-next-line no-undef
  if (view.innerWidth < 1024) {
    return 'tablet';
  }
  return 'desktop';
}
var PostsLayout = function PostsLayout(_ref2) {
  var layout = _ref2.layout,
    items = _ref2.items;
  var ref = useRef();
  var _useState = useState('desktop'),
    _useState2 = _slicedToArray(_useState, 2),
    currentViewPort = _useState2[0],
    setCurrentViewPort = _useState2[1];
  var layouts = {
    GRID: {
      '> *': {
        gridColumnEnd: 'span 6'
      },
      '@media $md_lte': {
        display: 'block'
      }
    },
    MOSAIC: {
      '> *:nth-child(1)': {
        gridColumnEnd: 'span 6',
        gridRowEnd: "span " + (items.length - 1)
      },
      '> *:nth-child(n+2)': {
        gridColumnEnd: 'span 6'
      },
      '@media $lg_lte': {
        '> *:nth-child(1)': {
          gridColumnEnd: 'span 12',
          gridRowEnd: 'span 1'
        },
        '> *:nth-child(n+2)': {
          gridColumnEnd: 'span 12'
        }
      }
    },
    INLINE: {
      display: items.length > 3 ? 'block' : 'grid',
      '> *': {
        gridColumnEnd: 'span 4'
      },
      '@media $lg_lte': {
        display: 'block'
      }
    },
    CAROUSEL: {
      display: 'block'
    }
  };
  useEffect(function () {
    var ownerDocument = ref.current.ownerDocument;
    var defaultView = ownerDocument.defaultView;
    // eslint-disable-next-line no-undef
    var onResize = function onResize() {
      setCurrentViewPort(getViewPort(defaultView));
    };
    var debouncedOnResize = _debounce(onResize, 100);
    setCurrentViewPort(getViewPort(defaultView));
    // eslint-disable-next-line no-undef
    defaultView.addEventListener('resize', debouncedOnResize);
    defaultView.addEventListener('orientationchange', debouncedOnResize);
    return function () {
      // eslint-disable-next-line no-undef
      defaultView.removeEventListener('resize', debouncedOnResize);
      defaultView.removeEventListener('orientationchange', debouncedOnResize);
    };
  }, []);
  var PostsItems = function PostsItems() {
    return items.map(function (item) {
      return /*#__PURE__*/React.createElement(PostCard, _extends({}, item, {
        key: item.id
      }));
    });
  };
  var renderControls = function renderControls(leftButtonProps, rightButtonProps) {
    if (currentViewPort !== 'mobile') {
      return /*#__PURE__*/React.createElement(Box, {
        sx: {
          position: 'absolute',
          top: '100%',
          right: '0',
          display: 'grid',
          gridAutoFlow: 'column',
          gridColumnGap: '$spacing.sm',
          alignItems: 'start'
        }
      }, /*#__PURE__*/React.createElement(IconButton, _extends({
        "aria-hidden": true,
        appearance: "circle",
        "aria-label": "Previous",
        icon: /*#__PURE__*/React.createElement(IconChevronLeft, null),
        size: "sm",
        tabIndex: "-1",
        title: "Previous items"
      }, leftButtonProps)), /*#__PURE__*/React.createElement(IconButton, _extends({
        "aria-hidden": true,
        appearance: "circle",
        "aria-label": "Next",
        icon: /*#__PURE__*/React.createElement(IconChevronRight, null),
        size: "sm",
        tabIndex: "-1",
        title: "Next items"
      }, rightButtonProps)));
    }
    return null;
  };
  if (layout === 'MOSAIC') {
    PostsItems = function PostItems() {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PostCard, _extends({}, items[0], {
        alignment: "vertical",
        size: "lg"
      })), currentViewPort === 'mobile' || currentViewPort === 'tablet' ? /*#__PURE__*/React.createElement(CustomCarousel, {
        renderNavigationControls: renderControls,
        slideWidth: 320,
        enableSnap: true,
        slideAlign: "start",
        scrollOffset: "sm"
      }, items.slice(1).map(function (item) {
        return /*#__PURE__*/React.createElement(PostCard, _extends({}, item, {
          key: item.id,
          alignment: "horizontal",
          size: "md",
          excerpt: undefined
        }));
      })) : items.slice(1).map(function (item) {
        return /*#__PURE__*/React.createElement(PostCard, _extends({}, item, {
          key: item.id,
          alignment: "horizontal",
          size: "md",
          excerpt: undefined
        }));
      }));
    };
  }
  if (layout === 'INLINE') {
    PostsItems = function PostItems() {
      return currentViewPort === 'mobile' || currentViewPort === 'tablet' || items.length > 3 ? /*#__PURE__*/React.createElement(CustomCarousel, {
        renderNavigationControls: renderControls,
        slideWidth: 320,
        enableSnap: true,
        slideAlign: "start",
        render: true,
        scrollOffset: "sm"
      }, items.map(function (item) {
        return /*#__PURE__*/React.createElement(PostCard, _extends({}, item, {
          key: item.id,
          alignment: "vertical",
          size: "md",
          excerpt: undefined
        }));
      })) : items.map(function (item) {
        return /*#__PURE__*/React.createElement(PostCard, _extends({}, item, {
          key: item.id,
          alignment: "vertical",
          size: "md",
          excerpt: undefined
        }));
      });
    };
  }
  if (layout === 'CAROUSEL') {
    PostsItems = function PostsItems() {
      // Check if items is less or equal to 1, if so, return a single post card
      if (items.length <= 1) {
        return /*#__PURE__*/React.createElement(PostCard, _extends({}, items[0], {
          key: items[0].id,
          alignment: "horizontal",
          size: "xl"
        }));
      }
      return /*#__PURE__*/React.createElement(CustomCarousel, {
        renderNavigationControls: renderControls,
        slideWidth: "100%",
        enableSnap: true,
        slideAlign: "start",
        scrollOffset: "sm"
      }, items.map(function (item) {
        return /*#__PURE__*/React.createElement(PostCard, _extends({}, item, {
          key: item.id,
          alignment: "horizontal",
          size: "xl"
        }));
      }));
    };
  }
  if (layout === 'GRID') {
    PostsItems = function PostsItems() {
      return currentViewPort === 'mobile' ? /*#__PURE__*/React.createElement(CustomCarousel, {
        renderNavigationControls: renderControls,
        slideWidth: 320,
        enableSnap: true,
        slideAlign: "start",
        scrollOffset: "sm"
      }, items.map(function (item) {
        return /*#__PURE__*/React.createElement(PostCard, _extends({}, item, {
          key: item.id,
          alignment: "horizontal",
          size: "md",
          excerpt: undefined
        }));
      })) : items.map(function (item) {
        return /*#__PURE__*/React.createElement(PostCard, _extends({}, item, {
          key: item.id,
          alignment: "horizontal",
          size: "md",
          excerpt: undefined
        }));
      });
    };
  }
  return /*#__PURE__*/React.createElement(Box, {
    ref: ref,
    sx: Object.assign({
      display: 'grid',
      gridTemplateColumns: 'repeat(12, 1fr)',
      gridGap: '$spacing.md'
    }, layouts[layout])
  }, /*#__PURE__*/React.createElement(PostsItems, null));
};
PostsLayout.propTypes = process.env.NODE_ENV !== "production" ? {
  layout: PropTypes.oneOf(['GRID', 'MOSAIC', 'INLINE', 'CAROUSEL']).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape(PostCard.propTypes))
} : {};
PostsLayout.defaultProps = {
  items: []
};
var PostsLayout$1 = PostsLayout;

export { PostsLayout$1 as default };
